import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ShoppingCart, Phone, Check, ChevronLeft, ChevronRight, MessageSquare, Info, ChevronDown } from 'lucide-react';
import { categories } from '../data/products';
import { useCart } from '../components/CartContext';
import { QuoteModal } from '../components/QuoteModal';
function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  className = ''
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    value: string;
    label: string;
    disabled?: boolean;
  }[];
  placeholder: string;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const selectedOption = options.find(opt => opt.value === value);
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-1492">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`w-full px-4 py-3 bg-brand-lightgray/50 dark:bg-[#1a1a1a] border border-stone-200 dark:border-[#555555] rounded-none text-brand-charcoal dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all flex items-center justify-between font-medium ${value ? '' : 'text-stone-400'}`} data-id="element-1493">
        <span className="truncate" data-id="element-1494">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-1495" />
      </button>

      <AnimatePresence data-id="element-1496">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className="absolute z-50 w-full mt-2 bg-white dark:bg-stone-900 border border-stone-200 dark:border-white/10 rounded-lg shadow-xl overflow-hidden max-h-60 overflow-y-auto" data-id="element-1497">
            {options.map(opt => <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
          if (!opt.disabled) {
            onChange(opt.value);
            setIsOpen(false);
          }
        }} className={`w-full text-left px-4 py-3 text-sm transition-colors ${opt.disabled ? 'text-stone-300 dark:text-white/30 cursor-not-allowed bg-stone-50 dark:bg-stone-900/50' : value === opt.value ? 'bg-brand-orange/20 text-brand-orange' : 'text-brand-charcoal dark:text-white hover:bg-stone-50 dark:hover:bg-white/10'}`} data-id="element-1498">
                {opt.label}
              </button>)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
const ProductImageCarousel = ({
  images,
  alt
}: {
  images: string[];
  alt: string;
}) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
  }, [images]);
  if (!images || images.length <= 1) {
    return <img src={images[0]} alt={alt} className="absolute inset-0 w-full h-full object-cover" data-id="element-1499" />;
  }
  return <div className="absolute inset-0 w-full h-full group" data-id="element-1500">
      <img src={images[idx]} alt={alt} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300" data-id="element-1501" />
      <div className="absolute inset-0 flex items-center justify-between p-4 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1502">
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === 0 ? images.length - 1 : i - 1);
      }} className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors" data-id="element-1503">
          <ChevronLeft className="w-5 h-5" data-id="element-1504" />
        </button>
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === images.length - 1 ? 0 : i + 1);
      }} className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors" data-id="element-1505">
          <ChevronRight className="w-5 h-5" data-id="element-1506" />
        </button>
      </div>
      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2" data-id="element-1507">
        {images.map((_, i) => <button key={i} onClick={e => {
        e.stopPropagation();
        setIdx(i);
      }} className={`w-2 h-2 rounded-full transition-colors ${i === idx ? 'bg-white' : 'bg-white/50 hover:bg-white/75'}`} data-id="element-1508" />)}
      </div>
    </div>;
};
export function Product() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const {
    addToCart
  } = useCart();
  const [quantity, setQuantity] = useState<string>('12');
  const [numLoads, setNumLoads] = useState<string>('');
  const [addedFlash, setAddedFlash] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [mobileOrderOpen, setMobileOrderOpen] = useState(false);
  // Build flat list of all products for prev/next navigation
  const allProducts = useMemo(() => {
    const list: {
      slug: string;
      name: string;
      categoryName: string;
    }[] = [];
    for (const cat of categories) {
      for (const prod of cat.products) {
        list.push({
          slug: prod.slug,
          name: prod.name,
          categoryName: cat.name
        });
      }
    }
    return list;
  }, []);
  // Find product and its category
  let product = null;
  let category = null;
  for (const cat of categories) {
    const found = cat.products.find(p => p.slug === slug);
    if (found) {
      product = found;
      category = cat;
      break;
    }
  }
  const currentIndex = allProducts.findIndex(p => p.slug === slug);
  const prevProduct = currentIndex > 0 ? allProducts[currentIndex - 1] : null;
  const nextProduct = currentIndex < allProducts.length - 1 ? allProducts[currentIndex + 1] : null;
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!product || !category) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" data-id="element-1509">
        <h1 className="text-2xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-1510">
          Product not found
        </h1>
        <button onClick={() => navigate('/')} className="text-brand-orange hover:underline" data-id="element-1511">
          Return to Home
        </button>
      </div>;
  }
  const isSpecialty = category.name === 'Specialty Rock';
  const isBark = product.name.toLowerCase().includes('bark');
  const isLimitedTo12 = product.name === 'Builders Sand' || product.name === 'Fill Dirt';
  const handleAddToCart = () => {
    if (quantity === 'multi' && numLoads === '3+') {
      setIsQuoteModalOpen(true);
      return;
    }
    let finalQuantity = Number(quantity);
    if (quantity === 'multi') {
      const deliveries = Number(numLoads);
      const fullLoadYards = isBark ? 15 : 12;
      finalQuantity = fullLoadYards * deliveries;
    }
    addToCart({
      category: category.name,
      product: product.name,
      quantity: finalQuantity,
      pricePerYard: product.pricePerYard
    });
    setAddedFlash(true);
    setTimeout(() => setAddedFlash(false), 2000);
    if (mobileOrderOpen) {
      setTimeout(() => setMobileOrderOpen(false), 1000);
    }
  };
  // Handle multi-load custom quote redirect
  useEffect(() => {
    if (quantity === 'multi' && numLoads === '3+') {
      setIsQuoteModalOpen(true);
      setNumLoads('');
      setQuantity(isBark ? '15' : '12');
    }
  }, [quantity, numLoads, isBark]);
  const renderOrderControls = () => <div className="space-y-6" data-id="element-1512">
      <div className="flex items-end justify-between border-b border-stone-100 dark:border-[#444444] md:border-stone-100 md:dark:border-[#444444] border-white/10 pb-6" data-id="element-1513">
        <div data-id="element-1514">
          <p className="text-sm text-white/70 md:text-brand-muted md:dark:text-stone-500 font-medium mb-1" data-id="element-1515">
            Price
          </p>
          <p className="text-3xl font-heading font-bold text-white md:text-brand-charcoal md:dark:text-white" data-id="element-1516">
            ${product.pricePerYard}{' '}
            <span className="text-base font-normal text-white/70 md:text-brand-muted md:dark:text-stone-500" data-id="element-1517">
              / yard
            </span>
          </p>
        </div>
      </div>

      <div className="space-y-4" data-id="element-1518">
        <label className="block text-sm font-bold text-white md:text-brand-charcoal md:dark:text-stone-300" data-id="element-1519">
          Select Quantity
        </label>
        <div className="flex flex-col sm:flex-row gap-4" data-id="element-1520">
          <div className="w-full sm:w-1/2" data-id="element-1521">
            <CustomSelect value={quantity} onChange={val => {
            setQuantity(val);
            if (val !== 'multi') setNumLoads('');
          }} placeholder="Quantity" options={isBark ? [{
            value: '7',
            label: '7 Yards'
          }, {
            value: '8',
            label: '8 Yards'
          }, {
            value: '9',
            label: '9 Yards'
          }, {
            value: '10',
            label: '10 Yards'
          }, {
            value: '11',
            label: '11 Yards'
          }, {
            value: '12',
            label: '12 Yards'
          }, {
            value: '13',
            label: '13 Yards'
          }, {
            value: '14',
            label: '14 Yards'
          }, {
            value: '15',
            label: '15 Yards (Full Load)'
          }, {
            value: 'multi',
            label: 'More than 1 load'
          }] : [{
            value: '6',
            label: '6 Yards'
          }, {
            value: '7',
            label: '7 Yards'
          }, {
            value: '8',
            label: '8 Yards'
          }, {
            value: '9',
            label: '9 Yards'
          }, {
            value: '10',
            label: '10 Yards'
          }, {
            value: '11',
            label: '11 Yards'
          }, {
            value: '12',
            label: '12 Yards (Full Load)'
          }, {
            value: 'multi',
            label: 'More than 1 load'
          }]} data-id="element-1522" />

            <AnimatePresence data-id="element-1523">
              {quantity === 'multi' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2
            }} className="overflow-visible mt-3" data-id="element-1524">
                  <CustomSelect value={numLoads} onChange={val => setNumLoads(val)} placeholder="How many loads?" options={[{
                value: '2',
                label: '2 Loads'
              }, {
                value: '3',
                label: '3 Loads'
              }, {
                value: '3+',
                label: '3+ Loads (Custom Quote)'
              }]} data-id="element-1525" />
                </motion.div>}
            </AnimatePresence>
          </div>

          {addedFlash ? <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex-1 flex items-center justify-center bg-[#76cc26]/10 dark:bg-[#76cc26]/10 text-[#76cc26] dark:text-[#76cc26] rounded-none font-bold border border-[#76cc26]/30 dark:border-[#76cc26]/30 py-3 sm:py-0" data-id="element-1526">
              <Check className="w-5 h-5 mr-2" data-id="element-1527" />
              Added to Cart
            </motion.div> : <button onClick={handleAddToCart} disabled={quantity === 'multi' && !numLoads} className={`flex-1 py-3 rounded-none font-bold transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed ${quantity && quantity !== 'multi' ? 'bg-brand-button hover:bg-brand-buttonHover text-white shadow-[0_0_12px_rgba(5,137,77,0.4)]' : quantity === 'multi' && numLoads && numLoads !== '3+' ? 'bg-brand-button hover:bg-brand-buttonHover text-white shadow-[0_0_12px_rgba(5,137,77,0.4)]' : 'bg-brand-charcoal dark:bg-white/10 text-white dark:text-white hover:bg-brand-charcoal/90 dark:hover:bg-white/15 border border-transparent dark:border-white/10'}`} data-id="element-1528">
              <ShoppingCart className="w-5 h-5 mr-2" data-id="element-1529" />
              Add to Cart
            </button>}
        </div>

        <AnimatePresence data-id="element-1530">
          {quantity === 'multi' && numLoads && numLoads !== '3+' && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="overflow-hidden" data-id="element-1531">
              <div className="flex items-start gap-2 mt-3 p-3 bg-brand-orange/10 border border-brand-orange/20 rounded-lg" data-id="element-1532">
                <Info className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" data-id="element-1533" />
                <p className="text-xs text-white/80 md:text-brand-charcoal md:dark:text-stone-300" data-id="element-1534">
                  Multi-load orders require separate deliveries. You will be
                  charged a delivery fee per load.
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>

        {quantity === 'multi' && !numLoads && <div className="flex items-start gap-2 mt-3 p-3 bg-brand-orange/10 border border-brand-orange/20 rounded-lg" data-id="element-1535">
            <Info className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" data-id="element-1536" />
            <p className="text-xs text-white/80 md:text-brand-charcoal md:dark:text-stone-300" data-id="element-1537">
              Please select how many loads you need.
            </p>
          </div>}
      </div>

      {quantity === 'multi' && numLoads === '3+' && <button onClick={() => setIsQuoteModalOpen(true)} className="w-full bg-brand-button hover:bg-brand-buttonHover text-white py-4 rounded-none font-bold text-lg transition-colors flex items-center justify-center" data-id="element-1538">
          Get a Custom Quote
        </button>}

      {/* Custom amount / bulk note */}
      <p className="text-xs text-white/70 md:text-brand-charcoal/70 md:dark:text-white/70 text-center" data-id="element-1539">
        Need a custom amount?{' '}
        <a href="tel:206-619-3742" className="text-brand-orange hover:underline" data-id="element-1540">
          Call us
        </a>{' '}
        or{' '}
        <Link to="/contact" className="text-brand-orange hover:underline" data-id="element-1541">
          contact us
        </Link>
        .
      </p>
    </div>;
  return <main className="py-12 md:py-20 bg-[#1b1b1b] dark:bg-[#1b1b1b] min-h-[calc(100vh-64px)] transition-colors duration-300" data-id="element-1542">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1543">
        {/* Top nav: Back + Prev/Next */}
        <div className="flex items-center justify-between mb-8" data-id="element-1544">
          <Link to="/#materials" className="inline-flex items-center text-sm font-medium text-brand-muted hover:text-brand-orange transition-colors" data-id="element-1545">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-1546" />
            Back to Materials
          </Link>
          <div className="flex items-center gap-2" data-id="element-1547">
            {prevProduct ? <Link to={`/shop/${prevProduct.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-muted dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange bg-white dark:bg-[#333333] border border-stone-200 dark:border-[#444444] rounded-lg transition-colors" title={prevProduct.name} data-id="element-1548">
                <ChevronLeft className="w-4 h-4" data-id="element-1549" />
                <span className="hidden sm:inline max-w-[120px] truncate" data-id="element-1550">
                  {prevProduct.name}
                </span>
                <span className="sm:hidden" data-id="element-1551">Prev</span>
              </Link> : <span className="px-3 py-1.5 text-sm text-stone-300 dark:text-stone-600 cursor-not-allowed" data-id="element-1552">
                <ChevronLeft className="w-4 h-4" data-id="element-1553" />
              </span>}
            {nextProduct ? <Link to={`/shop/${nextProduct.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-muted dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange bg-white dark:bg-[#333333] border border-stone-200 dark:border-[#444444] rounded-lg transition-colors" title={nextProduct.name} data-id="element-1554">
                <span className="hidden sm:inline max-w-[120px] truncate" data-id="element-1555">
                  {nextProduct.name}
                </span>
                <span className="sm:hidden" data-id="element-1556">Next</span>
                <ChevronRight className="w-4 h-4" data-id="element-1557" />
              </Link> : <span className="px-3 py-1.5 text-sm text-stone-300 dark:text-stone-600 cursor-not-allowed" data-id="element-1558">
                <ChevronRight className="w-4 h-4" data-id="element-1559" />
              </span>}
          </div>
        </div>

        {/* DESKTOP VIEW */}
        <div className="hidden md:flex bg-white dark:bg-[#333333] rounded-2xl border border-stone-200 dark:border-[#444444] overflow-hidden shadow-sm flex-row" data-id="element-1560">
          {/* Image */}
          <div className="w-1/2 relative min-h-[400px]" data-id="element-1561">
            <ProductImageCarousel images={product.images || [product.image]} alt={product.name} data-id="element-1562" />
          </div>

          {/* Content */}
          <div className="w-1/2 p-10 flex flex-col" data-id="element-1563">
            <div className="mb-2" data-id="element-1564">
              <span className="text-xs font-bold text-brand-muted uppercase tracking-wider" data-id="element-1565">
                {category.name}
              </span>
            </div>
            <h1 className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-1566">
              {product.name}
            </h1>
            <p className="text-sm text-brand-charcoal/80 dark:text-white/80 mb-6 leading-relaxed" data-id="element-1567">
              {product.description}
            </p>

            <div className="mt-auto" data-id="element-1568">
              {isSpecialty ? <div className="space-y-4" data-id="element-1569">
                  <div className="p-4 bg-brand-lightgray/50 dark:bg-[#222222] rounded-xl border border-stone-200 dark:border-[#444444]" data-id="element-1570">
                    <p className="text-sm text-brand-charcoal dark:text-stone-300" data-id="element-1571">
                      Pricing and availability for specialty rock varies. Please
                      contact us directly for a custom quote.
                    </p>
                  </div>
                  <a href="tel:206-619-3742" className="w-full bg-brand-charcoal dark:bg-white text-white dark:text-brand-charcoal hover:bg-brand-charcoal/90 dark:hover:bg-stone-200 py-4 rounded-xl font-bold text-lg transition-colors flex items-center justify-center" data-id="element-1572">
                    <Phone className="w-5 h-5 mr-2" data-id="element-1573" />
                    Call for Pricing
                  </a>
                </div> : renderOrderControls()}
            </div>
          </div>
        </div>

        {/* MOBILE VIEW */}
        <div className="md:hidden relative h-[calc(100vh-200px)] min-h-[500px] rounded-2xl overflow-hidden border border-stone-200/20" data-id="element-1574">
          {/* Background Image & Gradient */}
          <div className="absolute inset-0" data-id="element-1575">
            <ProductImageCarousel images={product.images || [product.image]} alt={product.name} data-id="element-1576" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent pointer-events-none" data-id="element-1577" />
          </div>

          {/* State 1: Photo View Content */}
          <AnimatePresence data-id="element-1578">
            {!mobileOrderOpen && <motion.div drag="y" dragConstraints={{
            top: -100,
            bottom: 0
          }} onDragEnd={(_e, info) => {
            if (info.offset.y < -50) setMobileOrderOpen(true);
          }} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: 20
          }} className="absolute inset-x-0 bottom-0 p-6 pb-8 flex flex-col z-10 cursor-grab active:cursor-grabbing touch-pan-y" data-id="element-1579">
                {/* Swipe up indicator */}
                <div className="w-full flex justify-center mb-4" data-id="element-1580">
                  <div className="w-12 h-1.5 bg-white/30 rounded-full" data-id="element-1581" />
                </div>

                <div className="mb-2" data-id="element-1582">
                  <span className="text-xs font-bold text-brand-muted uppercase tracking-wider" data-id="element-1583">
                    {category.name}
                  </span>
                </div>
                <h4 className="font-heading text-4xl font-bold mb-3 drop-shadow-lg text-white" data-id="element-1584">
                  {product.name}
                </h4>
                <p className="text-white/90 text-sm leading-relaxed mb-4 drop-shadow-md" data-id="element-1585">
                  {product.description}
                </p>

                <div className="pointer-events-auto" data-id="element-1586">
                  {isSpecialty ? <div className="space-y-3" data-id="element-1587">
                      <a href="tel:206-619-3742" className="w-full bg-white text-brand-charcoal py-3.5 rounded-xl font-bold flex items-center justify-center" data-id="element-1588">
                        <Phone className="w-5 h-5 mr-2" data-id="element-1589" /> Call for Pricing
                      </a>
                      <button onClick={() => setIsQuoteModalOpen(true)} className="w-full bg-brand-button text-white py-3.5 rounded-xl font-bold" data-id="element-1590">
                        Get a Custom Quote
                      </button>
                    </div> : <div className="flex items-center justify-between gap-4" data-id="element-1591">
                      <div data-id="element-1592">
                        <span className="font-heading text-3xl font-bold drop-shadow-lg text-white" data-id="element-1593">
                          ${product.pricePerYard}
                        </span>
                        <span className="text-white/70 text-sm ml-1 drop-shadow-md" data-id="element-1594">
                          / yard
                        </span>
                      </div>
                      <button onClick={e => {
                  e.stopPropagation();
                  setMobileOrderOpen(true);
                }} className="px-8 py-3 bg-brand-button hover:bg-brand-buttonHover text-white rounded-xl font-bold text-base shadow-[0_0_15px_rgba(5,137,77,0.5)] transition-colors shrink-0" data-id="element-1595">
                        Order
                      </button>
                    </div>}
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* State 2: Order Panel (Bottom Sheet) */}
          <AnimatePresence data-id="element-1596">
            {mobileOrderOpen && <>
                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} onClick={() => setMobileOrderOpen(false)} className="absolute inset-0 z-20 bg-black/40 backdrop-blur-sm" data-id="element-1597" />
                <motion.div drag="y" dragConstraints={{
              top: 0,
              bottom: 0
            }} dragElastic={0.2} onDragEnd={(_e, info) => {
              if (info.offset.y > 50) setMobileOrderOpen(false);
            }} initial={{
              y: '100%'
            }} animate={{
              y: 0
            }} exit={{
              y: '100%'
            }} transition={{
              type: 'spring',
              damping: 25,
              stiffness: 200
            }} className="absolute inset-x-0 bottom-0 z-30 bg-[#1a1a1a]/80 backdrop-blur-xl rounded-t-3xl border-t border-white/10 flex flex-col max-h-[85dvh]" onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()} data-id="element-1598">
                  {/* Drag Handle */}
                  <div className="w-full py-4 flex justify-center cursor-grab active:cursor-grabbing" data-id="element-1599">
                    <div className="w-12 h-1.5 bg-white/20 rounded-full" data-id="element-1600" />
                  </div>

                  <div className="px-6 pb-8 overflow-y-auto" data-id="element-1601">
                    <h4 className="font-heading text-xl font-bold text-white mb-4" data-id="element-1602">
                      {product.name}
                    </h4>

                    {category.name !== 'Specialty Rock' && product.pricePerYard > 0 && renderOrderControls()}
                  </div>
                </motion.div>
              </>}
          </AnimatePresence>
        </div>
      </div>

      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} data-id="element-1603" />
    </main>;
}